import React, { useState, useEffect } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Snackbar,
  Alert,
  Fade,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Reply, ContentCopy } from "@mui/icons-material";
import ChatHeader from "./ChatHeader";
import MessageBubble from "./MessageBubble";
import ChatInput from "./ChatInput";
import MessageSkeleton from "./MessageSkeleton";

const ChatWindow = ({
  contact,
  messages,
  text,
  setText,
  onSend,
  scrollRef,
  getInitials,
  loading,
  onBack,
  onUpdateContactName,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [contextMenu, setContextMenu] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "" });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (searchQuery.length > 2) {
      const match = messages.find((m) =>
        m.text.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (match) {
        const element = document.getElementById(`msg-${match._id}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.style.backgroundColor = alpha(primaryColor, 0.1);
          setTimeout(() => {
            element.style.backgroundColor = "transparent";
          }, 2000);
        }
      }
    }
  }, [searchQuery, messages]);

  const handleContextMenu = (event, message) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      message,
    });
  };

  const handleCopy = () => {
    if (contextMenu?.message?.text) {
      navigator.clipboard.writeText(contextMenu.message.text);
      setToast({ open: true, message: "Message copied to clipboard" });
    }
    setContextMenu(null);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") return;
    setToast({ ...toast, open: false });
  };

  const handleInternalSend = (data) => {
    // Handle both direct calls from keyboard (text) and ChatInput (object)
    const messageData = typeof data === "object" ? data : { text };

    if (!messageData.text?.trim() && !messageData.file && !messageData.audio) return;

    onSend({
      ...messageData,
      replyTo: replyTo?.text || null,
    });
    setReplyTo(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleInternalSend();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderDateSeparator = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    let label =
      date.toDateString() === today.toDateString()
        ? "TODAY"
        : date.toDateString() === yesterday.toDateString()
        ? "YESTERDAY"
        : date.toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });

    return (
      <Fade in timeout={300}>
        <Box sx={{ display: "flex", justifyContent: "center", my: 2.5 }}>
          <Typography
            sx={{
              bgcolor: "#ffffff",
              color: "#64748b",
              fontSize: "0.6875rem",
              fontWeight: 600,
              px: 2, 
              py: 0.75, 
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              border: "1px solid rgba(0, 0, 0, 0.04)",
            }}
          >
            {label}
          </Typography>
        </Box>
      </Fade>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        bgcolor: "#ffffff",
      }}
    >
      {/* Chat Header */}
      <ChatHeader
        contact={contact}
        getInitials={getInitials}
        onSearchChange={setSearchQuery}
        onBack={onBack}
        onUpdateContactName={onUpdateContactName}
      />

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2, // padding: 16px
          display: "flex",
          flexDirection: "column-reverse",
          // Custom scrollbar styling
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: alpha(primaryColor, 0.2),
            borderRadius: "10px",
            transition: "all 0.2s ease",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: alpha(primaryColor, 0.35),
          },
          // Firefox scrollbar
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(primaryColor, 0.2)} transparent`,
        }}
      >
        <div ref={scrollRef} />
        {loading ? (
          <MessageSkeleton />
        ) : (
          <>
            {[...messages].reverse().map((m, i, reversedArr) => {
              const nextMsg = reversedArr[i + 1];
              const isNewDate =
                nextMsg &&
                new Date(m.created_at).toDateString() !==
                  new Date(nextMsg.created_at).toDateString();
              const isLastInArray = i === reversedArr.length - 1;
              return (
                <React.Fragment key={m._id || i}>
                  <MessageBubble
                    m={m}
                    isMe={m.direction === "OUTBOUND"}
                    formatTime={formatTime}
                    onContextMenu={handleContextMenu}
                    searchQuery={searchQuery}
                  />
                  {(isLastInArray || isNewDate) &&
                    renderDateSeparator(m.created_at)}
                </React.Fragment>
              );
            })}
          </>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            bgcolor: "#1e293b",
            color: "#fff",
            minWidth: 180,
            borderRadius: "12px",
            boxShadow: "0 20px 40px -8px rgba(0, 0, 0, 0.25)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            overflow: "hidden",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setReplyTo(contextMenu.message);
            setContextMenu(null);
          }}
          sx={{
            py: 1.5, // padding-y: 12px
            px: 2, // padding-x: 16px
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: alpha(primaryColor, 0.2),
            },
          }}
        >
          <Reply sx={{ mr: 1.5, fontSize: 18 }} /> Reply
        </MenuItem>
        <MenuItem
          onClick={handleCopy}
          sx={{
            py: 1.5, // padding-y: 12px
            px: 2, // padding-x: 16px
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: alpha(primaryColor, 0.2),
            },
          }}
        >
          <ContentCopy sx={{ mr: 1.5, fontSize: 18 }} /> Copy
        </MenuItem>
      </Menu>

      {/* Chat Input */}
      <ChatInput
        text={text}
        setText={setText}
        onSend={handleInternalSend}
        replyTo={replyTo}
        setReplyTo={setReplyTo}
        contact={contact}
        handleKeyPress={handleKeyPress}
        conversationId={contact?._id}
      />

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            bgcolor: primaryColor,
            borderRadius: "12px",
            fontWeight: 600,
            boxShadow: `0 4px 14px ${alpha(primaryColor, 0.35)}`,
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatWindow;
